import './styles.scss'
import React from 'react'
import Loader from 'Components/Loader'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import html2canvas from 'html2canvas'
import { Button } from 'primereact/button'
import { useTranslation } from 'react-i18next'
import { BlueColorMainLogoPng, OnlyLogoPng, CoffeewebPlayStoreQRcode, CoffeewebAppStoreQRcode } from 'Assets/Icons'
import { commonMethods } from 'Utils/commonMethods'
import { getLocalAppSettings } from 'Utils/LocalStorageHandler'

const PreviewTradeInvoice = ({ invoiceDetails }) => {
  const grandTotal = invoiceDetails?.invoiceItems?.reduce((total, item) => total + (item.totalWithGst ?? 0), 0)

  const { t } = useTranslation()
  const appSettings = getLocalAppSettings()
  const { utcToLocalShortDate, numberToWords } = commonMethods

  const handleDownload = async () => {
    const invoiceElement = document.getElementById('invoice')

    const canvas = await html2canvas(invoiceElement, {
      scale: 1.5,
      useCORS: true
    })

    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF('p', 'mm', 'a4')

    const pdfWidth = pdf.internal.pageSize.getWidth()
    const ratio = canvas.height / canvas.width
    const imgHeight = pdfWidth * ratio

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight)

    // 🔽 Format date and time
    const now = new Date()
    const day = String(now.getDate()).padStart(2, '0')
    const month = now.toLocaleString('default', { month: 'short' }) // e.g., Jun
    const year = now.getFullYear()

    let hours = now.getHours()
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'

    hours = hours % 12 || 12

    const timeStr = `${String(hours).padStart(2, '0')}_${minutes} ${ampm}`
    const dateStr = `${day}-${month}-${year}`

    const fileName = `Invoice - ${dateStr} - ${timeStr}.pdf`

    pdf.save(fileName)
  }

  return (
    <div className="preview-invoice-wrapper">
      <div className="download-invoice-button">
        <Button className="primary-buttom-style" label={t('DOWNLOAD_INVOICE_PDF')} size="md" onClick={handleDownload} />
      </div>
      <div className="invoice-box-wrapper">
        <div className="invoice-box" id="invoice">
          <div className="invoice-border">
            <table className="invoice-detail-table">
              <tbody>
                <tr>
                  <td>
                    <b>{t('INVOICE_NUMBER')}:</b> {invoiceDetails.invoiceNumber}
                  </td>
                  <td>{t('BILL_OF_SUPPLY')}</td>
                  <td>
                    <b>{t('INVOICE_DATE')}:</b> {utcToLocalShortDate(invoiceDetails.invoiceDate)}
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="party-detail-table">
              <tbody>
                <tr>
                  <td>
                    <b>{t('SELLER')}</b>
                  </td>
                  <td rowSpan={2}>
                    <p>{t('CONTRACT_AND_DELIVERY_DETAILS')}:</p>
                    <p>
                      {t('CONTRACT_NUMBER')}: {invoiceDetails.contractDetails.contractNumber}
                    </p>
                    <p>
                      {t('CONTRACT_DATE')}: {utcToLocalShortDate(invoiceDetails.contractDetails.contractDate)}
                    </p>
                    <p>
                      {t('VEHICLE_NUMBER')}: {invoiceDetails.contractDetails.vehicleNumber}
                    </p>
                  </td>
                  <td>
                    <b>{t('BUYER')}</b>
                  </td>
                </tr>
                <tr>
                  <td className="primary-color">
                    <p className="company-name seller-company-name">
                      <img src={OnlyLogoPng} alt="" />
                      CoffeeWeb Technologies Pvt Ltd
                    </p>
                    <p>No 13, SS Towers, 3rd Floor</p>
                    <p>13th Cross, Sadashivanagar</p>
                    <p>Bengaluru, Karnataka - 560080</p>
                    <p>India</p>
                    <p>
                      {t('GSTIN')}: {appSettings?.gstNumber}
                    </p>
                    <p>
                      {t('PHONE_NO')}: {appSettings?.tradingInvoicePhoneNumber}
                    </p>
                    <p>
                      {t('E_Mail')}: {appSettings?.tradingInvoiceSender}
                    </p>
                  </td>
                  <td>
                    <p className="company-name primary-color">{invoiceDetails?.buyerCompanyDetails?.companyName}</p>

                    <p>{[invoiceDetails?.buyerCompanyDetails?.address?.buyerBuilding, invoiceDetails?.buyerCompanyDetails?.address?.buyerArea].filter(Boolean).join(', ')}</p>

                    <p>
                      {[invoiceDetails?.buyerCompanyDetails?.address?.buyerCity, invoiceDetails?.buyerCompanyDetails?.address?.buyerState].filter(Boolean).join(', ')}
                      {invoiceDetails?.buyerCompanyDetails?.address?.buyerPincode ? ` - ${invoiceDetails.buyerCompanyDetails.address.buyerPincode}` : ''}
                    </p>

                    <p>{invoiceDetails?.buyerCompanyDetails?.address?.buyerCountry}</p>
                    {invoiceDetails.buyerCompanyDetails?.buyerGstin && (
                      <p>
                        {t('GSTIN')}: {invoiceDetails.buyerCompanyDetails?.buyerGstin}
                      </p>
                    )}
                    <p>
                      {t('PHONE_NO')}: {invoiceDetails.buyerCompanyDetails.phoneNumber}
                    </p>
                    <p>
                      {t('EMAIL')}: {invoiceDetails.buyerCompanyDetails.email}
                    </p>
                    <p>
                      {t('CONTACT_PERSON')}: {invoiceDetails.buyerCompanyDetails.contactPerson}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="list-item-table">
              <thead>
                <tr>
                  <th>SL No</th>
                  <th>{t('DeSCRIPTION_OF_GOODS')}</th>
                  <th>{t('HSN_SAN')}</th>
                  <th>{t('QUANTITY_IN_KGS')}</th>
                  <th>{t('RATE_PER_KG')}</th>
                  {invoiceDetails.applyGst && <th>{t('GST_PERCENTAGE')}</th>}
                  <th>{t('AMOUNT')}</th>
                </tr>
              </thead>
              <tbody>
                {invoiceDetails?.invoiceItems?.map((item, index) => (
                  <tr key={index} className={invoiceDetails.invoiceItems.length === index ? '' : 'table-item-row'}>
                    <td>{index + 1}</td>
                    <td>{item.description}</td>
                    <td className="text-right-right">{item.hsnCode}</td>
                    <td className="text-right-right">{item.quantityInKgs}</td>
                    <td className="text-right-right">{item.ratePerKg}</td>
                    {invoiceDetails?.applyGst && <td className="text-right-right">{item?.gstPercentage} %</td>}
                    <td className="text-right-right">₹ {item.totalWithGst?.toLocaleString('en-IN') || 0}</td>
                  </tr>
                ))}
                {Array.from({ length: Math.max(0, 5 - invoiceDetails.invoiceItems.length) }).map((_, idx) => (
                  <tr key={`empty-${idx}`} className="table-item-row">
                    {invoiceDetails?.applyGst ? (
                      <>
                        {Array.from({ length: 7 }).map((_, i) => (
                          <td key={i}></td>
                        ))}
                      </>
                    ) : (
                      <>
                        {Array.from({ length: 6 }).map((_, i) => (
                          <td key={i}></td>
                        ))}
                      </>
                    )}
                  </tr>
                ))}
                <tr>
                  <td></td>
                  <td>
                    <b>{t('TOTAL')}</b>
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  {invoiceDetails?.applyGst && <td></td>}
                  <td>
                    <b>₹ {grandTotal?.toLocaleString('en-IN')}</b>
                  </td>
                </tr>
                <tr>
                  <td colSpan={4}>{t('AMOUNT_CHARGABLE_IN_WORDS')}</td>
                  <td colSpan={4}>
                    <b>{t('TAXABLE_VALUE')}</b>
                  </td>
                </tr>
                <tr>
                  <td colSpan={4} className="amount-in-words">
                    {numberToWords(grandTotal)}
                  </td>
                  <td colSpan={4}>₹ {grandTotal?.toLocaleString('en-IN')}</td>
                </tr>
              </tbody>
            </table>

            <div className="diclaration-and-sign-wrapper">
              <div>
                <b>{t('DECLARATION')}</b>
                <p>{t('WE_DECLARE_THAT_THIS_INVOICE_SHOWS_THE_ACTUAL_PRICE_OF_THE_GOODS')}</p>
              </div>
              <div>
                {/* <img style={{ width: '140px' }} src="https://cdn.prod.website-files.com/61d7de73eec437f52da6d699/62161cf7328ad280841f653f_esignature-signature.png" alt="" /> */}
                <p>
                  <b>{t('AUTHORISED_SIGNATORY')}</b>
                </p>
              </div>
            </div>

            <div className="app-links">
              <img className="logo" src={BlueColorMainLogoPng} alt="Company Logo" />
              <div className="qr-code-wrapper">
                <div>
                  <div className="primary-color">{t('PLAY_STORE')}</div>
                  <div>
                    <img src={CoffeewebPlayStoreQRcode} alt="QR" />
                  </div>
                </div>
                <div>
                  <div className="primary-color">{t('APP_STORE')}</div>
                  <div>
                    <img src={CoffeewebAppStoreQRcode} alt="QR" />
                  </div>
                </div>
              </div>
              <a className="domain-link" href="https://www.coffeeweb.com" target="_blank" rel="noreferrer">
                {t('WWW_COFFEEWEB_COM')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Loader(PreviewTradeInvoice)
